<script setup>
// This is the single component which renders all the widgets supported by the application.
import { cloneDeep, isEqual, isNil, uniq } from 'lodash-es';
import { storeToRefs } from 'pinia';
import { computed, nextTick, onBeforeUnmount, onMounted, reactive } from 'vue';
import { useModal } from 'vue-final-modal';
import BiCreateRichTextWidget from '~/bi/components/bi-create-rich-text-widget.vue';
import BiCreateWidget from '~/bi/components/bi-create-widget.vue';
import BiDuplicateWidgetModal from '~/bi/components/widgets/bi-duplicate-widget-modal.vue';
import BiWidgetQuickFilters from '~/bi/components/widgets/bi-widget-quick-filters.vue';
import BiTableWidget from '~/bi/components/widgets/table-widget/bi-table-widget.vue';
import { useBiExport } from '~/bi/composables/bi-export.composable';
import { useBiStore } from '~/bi/store/bi.store';
import { useCommonImports } from '~/common/composables/common-imports.composable';
import useEmitter from '~/common/composables/useEmitter';
import { sleep } from '~/common/utils/common.utils';
import { csvInjectionProtector, sanitizeObjectForCSVInjection } from '~/common/utils/common.utils.js';

const props = defineProps({
  // 'preview' for the preview mode
  widgetId: {
    type: String,
    required: true,
  },
  // Required for both preview and dashboard grid mode
  config: {
    type: Object,
    required: true,
  },
  // Only required for the preview mode
  widgetBuilderData: {
    type: Array,
    required: false,
  },
  // Only required for the preview mode
  widgetBuilderMetadata: {
    type: Object,
    required: false,
  },
  // Required in dashboard grid mode (ALWAYS), Required for the preview mode if the chart type is number_chart
  widgetName: {
    type: String,
    required: false,
  },
  // Only required for the dashboard grid mode and tells if the grid is in editing mode
  isEditing: {
    type: Boolean,
    default: false,
  },
  // Only required in the grid mode and this config doesn't include things like global filters
  originalConfig: {
    type: Object,
  },
});

const emit = defineEmits(['lastParsedConfig']);

const emitter = useEmitter();

const bi_store = useBiStore();
const { current_grid_layout, get_applied_filters_for_dashboard, selected_dashboard_details } = storeToRefs(bi_store);

const { $t } = useCommonImports();

const { exportWidget } = useBiExport();

const state = reactive({
  widget_data: [],
  widget_metadata: {},
  widget_sql_query: '',
  is_loading: false,
  table_resize_observer: null,
  show_table_widget: false,
  table_height: 0,
  is_ignoring_next_config_change: false,
  drill_down: {
    active: false,
    data: null,
    config: null,
  },
  is_menu_open: false,
});

let resize_timeout = null;

const edit_widget = useModal({
  component: BiCreateWidget,
  attrs: {
    onClose() {
      edit_widget.close();
    },
  },
});

const edit_rich_text_widget = useModal({
  component: BiCreateRichTextWidget,
  attrs: {
    onClose() {
      edit_rich_text_widget.close();
    },
  },
});

const duplicate_widget_modal = useModal({
  component: BiDuplicateWidgetModal,
  attrs: {
    onClose() {
      duplicate_widget_modal.close();
    },
  },
});

const card_styles = computed(() => {
  const styles = {};
  if (props.config.chart.type === 'number_chart') {
    styles['background-color'] = props.config.chart.number_widget_background_color;
  }
  else if (props.config.chart.type === 'rich_text' && !props.config.chart.is_transparent_background) {
    styles['background-color'] = props.config.chart.background_color;
  }
  return styles;
});

const download_options = computed(() => {
  return [
    {
      label: $t('Export as PDF'),
      on_click: async () => {
        exportWidget(props.widgetId, props.widgetName, 'pdf');
      },
    },
    {
      label: $t('Export as XLSX'),
      on_click: async () => {
        await exportTableAsXLSX();
      },
    },
    {
      label: $t('Export as PNG'),
      on_click: async () => {
        exportWidget(props.widgetId, props.widgetName, 'png');
      },
    },
  ];
});

const global_filters_applied_to_widget = computed(() => {
  const global_filters = selected_dashboard_details.value?.meta?.global_filters || [];
  return global_filters.filter(filter => Object.keys(filter.widgets).includes(props.widgetId) && get_applied_filters_for_dashboard.value[filter.uid]).map(global_filter => global_filter.general.filter_name);
});

const global_filters_applied_to_widget_tooltip = computed(() => {
  if (global_filters_applied_to_widget.value.length === 1)
    return `"${global_filters_applied_to_widget.value[0]}" ${$t('filter applied')}`;
  return `"${global_filters_applied_to_widget.value.join('", "')}" ${$t('filters applied')}`;
});

const display_widget_data = computed(() => {
  if (state.drill_down.active) {
    return state.drill_down.data;
  }
  return state.widget_data;
});

async function exportTableAsXLSX() {
  let allowed_fields = [];
  if (['table'].includes(props.config.chart.type)) {
    allowed_fields = Object.keys(props.config.chart.columns_map).filter(col => props.config.chart.columns_map[col].visible);
  }
  else if (props.config.chart.type === 'pivot_table') {
    allowed_fields = [
      ...(props.config.chart.pivot_rows || []),
      ...(props.config.chart.pivot_columns || []),
      ...((props.config.chart.pivot_values || []).map(item => item.column)),
    ];
  }
  else if (props.config.chart.type === 'number_chart') {
    allowed_fields = [props.config.chart.layout_category, ...props.config.chart.layout_values.map(item => item.value)];
  }
  else {
    allowed_fields = [
      props.config.chart.data.category,
      ...(props.config.chart.data.values || []),
      ...(props.config.chart.data.stackBy !== true ? [props.config.chart.data.stackBy] : []),
    ].filter(Boolean);
  }
  allowed_fields = uniq(allowed_fields);

  const ExcelJS = await import('exceljs');
  const { saveAs } = await import('file-saver');

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Sheet 1');

  // Header row
  worksheet.addRow(allowed_fields.map(key => csvInjectionProtector((key || '').toString())));
  worksheet.getRow(1).font = { bold: true };

  // Data rows — sanitize each row object before extracting values
  const sanitized_data = state.widget_data.map(row => sanitizeObjectForCSVInjection(row));
  sanitized_data.forEach((row) => {
    worksheet.addRow(allowed_fields.map(f => (row[f] ?? '')));
  });

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `${props.widgetName || 'Untitled'}.xlsx`);
}

function editWidget(view) {
  edit_widget.patchOptions({
    attrs: {
      mode: 'edit',
      view,
      widgetName: props.widgetName,
      config: cloneDeep(props.originalConfig),
      widgetBuilderData: state.widget_data,
      widgetBuilderMetadata: state.widget_metadata,
      widgetSqlQuery: state.widget_sql_query,
      widgetId: props.widgetId,
      onClose() {
        edit_widget.close();
      },
    },
  });
  edit_widget.open();
}

function editRichTextWidget() {
  edit_rich_text_widget.patchOptions({
    attrs: {
      mode: 'edit',
      chartConfig: props.config.chart,
      widgetId: props.widgetId,
      onClose() {
        edit_rich_text_widget.close();
      },
    },
  });
  edit_rich_text_widget.open();
}

function openDuplicateWidgetModal() {
  const current_widget_layout = current_grid_layout.value.find(widget => widget.uid === props.widgetId);
  duplicate_widget_modal.patchOptions({
    attrs: {
      widgetId: props.widgetId,
      widgetConfig: cloneDeep(props.originalConfig),
      widgetName: props.widgetName,
      widgetLayout: current_widget_layout ? { w: current_widget_layout.w, h: current_widget_layout.h } : {},
      onClose() {
        duplicate_widget_modal.close();
      },
    },
  });
  duplicate_widget_modal.open();
}

function deleteWidget() {
  bi_store.deleteWidget(props.widgetId);
}

async function renderWidget() {
  if (['table', 'pivot_table'].includes(props.config.chart.type)) {
    await nextTick();
    state.table_height = calculateTableHeight();
    await sleep(300);
    state.show_table_widget = true;
  }
}

function handleResizeEnd() {
  if (props.widgetId !== 'preview' && ['table', 'pivot_table'].includes(props.config.chart.type)) {
    state.table_height = calculateTableHeight();
  }
}

function calculateTableHeight() {
  const containerElement = props.widgetId === 'preview' ? document.querySelector('.widget-preview-container') : document.getElementById(`container-${props.widgetId}`);
  if (!containerElement)
    return 0;

  const containerRect = containerElement.getBoundingClientRect();

  let usedHeight = 0;

  const headerElement = containerElement.querySelector('.bi-widget-header');
  if (headerElement) {
    usedHeight += headerElement.offsetHeight + 8;
  }

  const quickFiltersElement = containerElement.querySelector('.bi-widget-quick-filters');
  if (quickFiltersElement) {
    usedHeight += quickFiltersElement.offsetHeight;
  }

  const bottomSpacing = 28 + (props.widgetId === 'preview' ? 25 : 0);

  return Math.max(0, containerRect.height - usedHeight - bottomSpacing);
}

function applyChartFilters(drill_down_config, options) {
  if (options.type === 'chart' && options.filters?.length > 0) {
    const alias_stage_index_map = {};
    drill_down_config.query.stages.forEach((stage, index) => {
      stage.columns.forEach((column) => {
        if (isNil(alias_stage_index_map[column.alias]))
          alias_stage_index_map[column.alias] = index;
      });
    });
    options.filters.forEach((filter) => {
      const stage_index = alias_stage_index_map[filter.field];
      if (stage_index !== undefined) {
        drill_down_config.query.stages[stage_index].filters.push(filter);
      }
    });
  }
  return drill_down_config;
}

function applyBinChange(drill_down_config, options) {
  if (options.binChange?.target_bin && options.binChange?.column?.alias) {
    let bin_field, bin_index;
    drill_down_config.query.stages.forEach((stage, index) => {
      stage.columns.forEach((column) => {
        if (column.alias === options.binChange?.column?.alias) {
          column.dateBin = options.binChange?.target_bin;
          bin_field = bin_field || column.field;
          bin_index = bin_index ?? index;
        }
      });
    });
    if (!isNil(bin_index)) {
      const expr = `between([${bin_field}], date('${options.binChange.date_range[0]}'), date('${options.binChange.date_range[1]}'))`;
      drill_down_config.query.stages[bin_index].filters.push({ expr });
    }
  }
  return drill_down_config;
}

async function handleWidgetDrillDown(active, drill_down_config, options) {
  drill_down_config = applyChartFilters(drill_down_config, options);
  drill_down_config = applyBinChange(drill_down_config, options);
  state.is_loading = true;
  state.drill_down.active = active;
  state.drill_down.config = drill_down_config;
  const data = await bi_store.getWidgetData(drill_down_config.query.stages, props.widgetId, null, props.config.quick_filters);
  state.drill_down.data = data.data;
  state.is_loading = false;
}

async function handleQuickFiltersApply(quick_filters) {
  if (props.widgetId === 'preview') {
    state.is_loading = true;
    const data = await bi_store.getWidgetData(props.config.query.stages, props.widgetId, null, quick_filters);
    state.widget_data = data.data;
    state.widget_metadata = data.metadata;
    state.is_loading = false;
  }
}

// IMPORTANT FUNCTION: Make changes with caution
watch(
  () => cloneDeep(props.config),
  async (new_config, old_config) => {
    if (
      !props.widgetId
      || !props.config.query
      || !props.config.chart
      || (new_config.chart?.type === 'rich_text')
      || (props.widgetId !== 'preview' && (isEqual(new_config, old_config) || state.is_ignoring_next_config_change))
    ) {
      state.is_ignoring_next_config_change = false;
      if (new_config.chart?.type === 'rich_text' && props.widgetId !== 'preview') {
        bi_store.markWidgetRendered(props.widgetId);
      }
      return;
    }
    state.is_loading = true;
    if (new_config?.chart?.type !== old_config?.chart?.type) {
      state.show_table_widget = false;
    }
    if (props.widgetId === 'preview') {
      state.widget_data = props.widgetBuilderData;
      state.widget_metadata = props.widgetBuilderMetadata;
    }
    else {
      if (state.drill_down.active) {
        const modified_drill_down_config = cloneDeep(state.drill_down.config);
        modified_drill_down_config.quick_filters = props.config.quick_filters;
        const drill_down_data = await bi_store.getWidgetData(modified_drill_down_config.query.stages, props.widgetId, null, modified_drill_down_config.quick_filters);
        state.drill_down.data = drill_down_data.data;
      }
      const data = await bi_store.getWidgetData(props.config.query.stages, props.widgetId, null, props.config.quick_filters);
      state.widget_data = data.data;
      state.widget_metadata = data.metadata;
      state.widget_sql_query = data.sql_query;
    }
    state.is_loading = false;
    await nextTick();
    renderWidget();
    if (props.widgetId !== 'preview') {
      bi_store.markWidgetRendered(props.widgetId);
    }
  },
  { deep: true, immediate: true },
);

watch(() => [state.show_table_widget, props.widgetId], async () => {
  if (state.show_table_widget && props.widgetId !== 'preview' && ['table', 'pivot_table'].includes(props.config.chart.type)) {
    await nextTick();
    const containerElement = document.getElementById(`container-${props.widgetId}`);
    if (containerElement) {
      state.table_resize_observer = new ResizeObserver(() => {
        if (resize_timeout) {
          clearTimeout(resize_timeout);
        }

        resize_timeout = setTimeout(() => {
          handleResizeEnd();
          resize_timeout = null;
        }, 500);
      });
      state.table_resize_observer.observe(containerElement);
    }
  }
}, { immediate: true });

onMounted(() => {
  emitter.on(`ignoreNextConfigChange_${props.widgetId}`, () => {
    state.is_ignoring_next_config_change = true;
  });
});

onBeforeUnmount(() => {
  if (state.table_resize_observer) {
    state.table_resize_observer.disconnect();
    state.table_resize_observer = null;
  }

  if (resize_timeout) {
    clearTimeout(resize_timeout);
    resize_timeout = null;
  }

  emitter.off(`ignoreNextConfigChange_${props.widgetId}`);
});
</script>

<template>
  <div
    :id="`container-${props.widgetId}`"
    class="h-full w-full group relative flex flex-col"
    :class="{
      'p-0.5': props.widgetId === 'preview',
      'p-3 bg-white border border-gray-200 rounded': props.widgetId !== 'preview' || props.config.chart.type === 'number_chart',
      '!border-transparent !bg-transparent': props.config.chart.type === 'rich_text' && props.config.chart.is_transparent_background,
      'h-fit !p-0 !pr-2': props.config.chart.type === 'rich_text',
      '!w-fit !h-fit min-w-[300px]': props.config.chart.type === 'number_chart' && props.widgetId === 'preview',
      'scrollbar': props.config.chart.type === 'number_chart',
    }"
    v-bind="{
      style: card_styles,
    }"
    @click="handleContainerClick"
  >
    <div v-if="(props.widgetId !== 'preview' || props.config.chart.type === 'number_chart') && props.config.chart.type !== 'rich_text'" class="w-[calc(100%-8px)] min-h-[32px] flex items-center justify-between gap-3 mb-2 flex-shrink-0 bi-widget-header">
      <div class="flex items-center gap-1 min-w-0 flex-1">
        <div
          v-if="state.drill_down.active"
          class="flex items-center gap-2 cursor-pointer hover:underline text-sm font-semibold text-gray-700"
          @click="state.drill_down.active = false"
        >
          <IconHawkArrowLeft class="bi-exportable-icon" />
          {{ $t('Back to chart') }}
        </div>
        <div
          v-else
          class="text-sm font-medium text-gray-900 text-ellipsis overflow-hidden whitespace-nowrap widget-title"
          :style="`color: ${props.config.chart.number_widget_foreground_color || '#101828'}`"
        >
          {{ props.widgetName }}
        </div>
        <IconHawkFilterFunnelRed
          v-if="global_filters_applied_to_widget.length"
          v-tippy="{
            content: global_filters_applied_to_widget_tooltip,
            placement: 'top',
          }"
          class="w-4 h-4 flex-shrink-0"
        />
      </div>
      <template v-if="!state.is_loading">
        <div v-if="props.isEditing" class="flex items-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity ignore-widget-drag">
          <IconHawkEditFive
            v-tippy="{
              content: $t('Edit data'),
              placement: 'top',
            }"
            class="w-4 h-4 text-gray-600 outline-0 cursor-pointer"
            @click="editWidget('data-builder')"
          />
          <IconHawkBarChartTen
            v-tippy="{
              content: $t('Edit chart'),
              placement: 'top',
            }"
            class="w-4 h-4 text-gray-600 outline-0 cursor-pointer"
            @click="editWidget('chart-builder')"
          />
          <IconHawkCopyFour
            v-tippy="{
              content: $t('Duplicate'),
              placement: 'top',
            }"
            class="w-4 h-4 text-gray-600 outline-0 cursor-pointer"
            @click="openDuplicateWidgetModal"
          />
          <IconHawkTrashThree
            v-tippy="{
              content: $t('Delete'),
              placement: 'top',
            }"
            class="w-4 h-4 text-gray-600 outline-0 cursor-pointer"
            @click="deleteWidget"
          />
        </div>
        <div v-else-if="props.widgetId !== 'preview'" class="flex items-center gap-3 transition-opacity" :class="state.is_menu_open ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'">
          <HawkMenu additional_trigger_classes="!ring-0 !border-0 mt-1.5" :teleport="true" :items="download_options" @open="state.is_menu_open = true" @close="state.is_menu_open = false">
            <template #trigger>
              <IconHawkDotsVertical
                class="w-4 h-4 text-gray-600 outline-0 cursor-pointer"
              />
            </template>
          </HawkMenu>
        </div>
      </template>
    </div>

    <BiWidgetQuickFilters
      v-if="(props.widgetId === 'preview') || (props.widgetId !== 'preview' && props.config.chart.type !== 'rich_text' && props.config.quick_filters?.length)"
      :widget-id="props.widgetId"
      :config="props.widgetId === 'preview' ? props.config : props.originalConfig"
      :widget-data="state.widget_data"
      @apply-filters="handleQuickFiltersApply"
    />

    <HawkLoader v-if="state.is_loading" container_class="flex-1 min-h-0" />
    <div v-else-if="['table', 'pivot_table'].includes(props.config.chart.type)" class="flex-1 min-h-0 overflow-hidden ignore-widget-drag">
      <BiTableWidget
        v-if="state.show_table_widget"
        v-bind="{
          ...(state.table_height > 0 ? {
            height: state.table_height,
          } : {}),
        }"
        :id="props.widgetId"
        :is-builder="false"
        :config="props.config"
        :data="display_widget_data"
        :widget-builder-metadata="state.widget_metadata"
        :is-preview="props.widgetId === 'preview'"
      />
    </div>
    <BiRichTextWidget
      v-else-if="props.config.chart.type === 'rich_text'"
      :widget-config="props.config"
      :is-editing="props.isEditing"
      @edit="editRichTextWidget"
      @duplicate="openDuplicateWidgetModal"
      @delete="deleteWidget"
    />
    <BiNumberChartWidget
      v-else-if="props.config.chart.type === 'number_chart'"
      :id="props.widgetId"
      :data="display_widget_data"
      :config="props.config"
      :is-preview="props.widgetId === 'preview'"
      class="ignore-widget-drag flex-1 min-h-0"
    />
    <BiDetailsCardWidget
      v-else-if="props.config.chart.type === 'details_card'"
      :id="props.widgetId"
      :data="display_widget_data"
      :config="props.config"
      :is-preview="props.widgetId === 'preview'"
      class="ignore-widget-drag flex-1 min-h-0"
    />
    <BiMapWidget
      v-else-if="props.config.chart.type === 'map'"
      :id="props.widgetId"
      class="ignore-widget-drag flex-1 min-h-0"
      :data="display_widget_data"
      :config="props.config"
      :is-preview="props.widgetId === 'preview'"
      @ignoring-next-config-change="state.is_ignoring_next_config_change = true"
    />
    <BiChartWidget
      v-else
      :widget-id="props.widgetId"
      :widget-name="props.widgetName"
      :config="state.drill_down.active ? state.drill_down.config : props.config"
      :widget-data="display_widget_data"
      :is-preview="props.widgetId === 'preview'"
      class="flex-1 min-h-0"
      @widget-drill-down="handleWidgetDrillDown"
      @last-parsed-config="(config) => emit('lastParsedConfig', config)"
    />
  </div>
</template>
